// Replays a phone magic-pointer gesture as the same synthetic pointer events
// a JioSphere / smart-TV touch pointer produces. The existing PointerScroll
// component (edge auto-scroll, hover focus, wheel) then handles it natively.

const clamp01 = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0.5;
  return Math.min(1, Math.max(0, n));
};

const makeData = (e, init) => Object.assign(e, init);

const createPointerEvent = (type, x, y, button) => {
  const init = {
    view: window,
    bubbles: true,
    cancelable: true,
    clientX: x,
    clientY: y,
    screenX: x,
    screenY: y,
    button: button || 0,
    buttons: button === 0 ? 1 : 0,
    pointerId: 1,
    pointerType: 'mouse',
    isPrimary: true,
    width: 1,
    height: 1,
    pressure: button === 0 ? 0.5 : 0,
  };

  try {
    if (window.PointerEvent) return new window.PointerEvent(type, init);
  } catch (err) { /* fall through */ }

  // Simple legacy path: assign the bits PointerScroll reads to a plain event.
  try {
    const evt = document.createEvent('Event');
    evt.initEvent(type, true, true);
    return makeData(evt, init);
  } catch (err) {
    return null;
  }
};

const createMouseEvent = (type, x, y, button) => {
  const init = {
    view: window,
    bubbles: true,
    cancelable: true,
    clientX: x,
    clientY: y,
    screenX: x,
    screenY: y,
    button: button || 0,
    buttons: button === 0 ? 1 : 0,
  };

  try {
    if (window.MouseEvent) return new window.MouseEvent(type, init);
  } catch (err) { /* fall through */ }

  try {
    const evt = document.createEvent('MouseEvents');
    evt.initMouseEvent(type, true, true, window, 0, x, y, x, y,
      false, false, false, false, button || 0, null);
    return evt;
  } catch (err) {
    return null;
  }
};

const targetAt = (x, y) => {
  try {
    return document.elementFromPoint(x, y) || document.body || document.documentElement;
  } catch (err) {
    return document.body || document.documentElement;
  }
};

const FOCUSABLE = '[data-focusable], a[href], button, [role="button"]';

// Hover focus: keep the TV element under the cursor focusable/activatable so
// OK / tap feels like a real remote pointer.
const focusAt = (x, y) => {
  const target = targetAt(x, y);
  if (!target || !target.closest) return;
  try {
    const el = target.closest(FOCUSABLE);
    if (el && !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || '')) {
      el.focus({ preventScroll: true });
    }
  } catch (err) {
    /* focus unsupported */
  }
};

// Direct scroll for remote wheel events. The synthetic WheelEvent path depends
// on PointerScroll being enabled, which TV WebViews may not trigger; scrolling
// the nearest scrollable ancestor keeps the remote's scroll rail reliable.
const pageScroller = () =>
  document.scrollingElement || document.documentElement || document.body;

const canScrollY = (el) => Boolean(el && el.scrollHeight > el.clientHeight + 4);
const canScrollX = (el) => Boolean(el && el.scrollWidth > el.clientWidth + 4);

const scrollableParent = (node, axis) => {
  let el = node;
  while (el && el.nodeType === 1 && el !== document.body && el !== document.documentElement) {
    try {
      const st = window.getComputedStyle(el);
      if (axis === 'y') {
        if (
          (st.overflowY === 'auto' || st.overflowY === 'scroll') &&
          el.scrollHeight > el.clientHeight + 4
        ) {
          return el;
        }
      } else if (
        (st.overflowX === 'auto' || st.overflowX === 'scroll') &&
        el.scrollWidth > el.clientWidth + 4
      ) {
        return el;
      }
    } catch (err) {
      /* computed style unavailable */
    }
    el = el.parentElement;
  }
  return null;
};

const applyScroll = (el, dx, dy) => {
  if (!el) return false;
  try {
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ top: dy || 0, left: dx || 0, behavior: 'instant' });
      return true;
    }
  } catch (err) {
    /* fall through */
  }
  try {
    el.scrollTop = (el.scrollTop || 0) + (dy || 0);
    el.scrollLeft = (el.scrollLeft || 0) + (dx || 0);
    return true;
  } catch (err) {
    return false;
  }
};

const scrollAt = (x, y, dx, dy) => {
  const target = targetAt(x, y);
  const root = pageScroller();
  let didScroll = false;

  if (dy) {
    const scroller = scrollableParent(target, 'y') || (canScrollY(root) ? root : null) || root;
    didScroll = applyScroll(scroller, 0, dy) || didScroll;
  }

  if (dx) {
    const scroller = scrollableParent(target, 'x') || (canScrollX(root) ? root : null);
    didScroll = applyScroll(scroller, dx, 0) || didScroll;
  }

  return didScroll;
};

const doDispatch = (target, event) => {
  if (!target || !event) return false;
  try {
    target.dispatchEvent(event);
    return true;
  } catch (err) {
    try { document.dispatchEvent(event); return true; } catch (e2) { return false; }
  }
};

export const dispatchRemotePointer = (msg) => {
  if (typeof window === 'undefined' || !msg || msg.type !== 'pointer') return false;

  const width = window.innerWidth || document.documentElement.clientWidth || 1280;
  const height = window.innerHeight || document.documentElement.clientHeight || 720;
  const x = clamp01(msg.x) * width;
  const y = clamp01(msg.y) * height;
  const phase = String(msg.phase || '').toLowerCase();

  if (phase === 'move') {
    focusAt(x, y);
    return doDispatch(targetAt(x, y), createPointerEvent('pointermove', x, y, 0));
  }
  if (phase === 'down') {
    focusAt(x, y);
    return doDispatch(targetAt(x, y), createPointerEvent('pointerdown', x, y, 0));
  }
  if (phase === 'up') {
    focusAt(x, y);
    return doDispatch(targetAt(x, y), createPointerEvent('pointerup', x, y, 0));
  }
  if (phase === 'click') {
    focusAt(x, y);
    const down = createPointerEvent('pointerdown', x, y, 0);
    const up = createPointerEvent('pointerup', x, y, 0);
    const target = targetAt(x, y);
    doDispatch(target, down);
    doDispatch(target, up);
    doDispatch(target, createMouseEvent('click', x, y, 0));
    return true;
  }
  if (phase === 'wheel') {
    const deltaX = Number(msg.deltaX) || 0;
    const deltaY = Number(msg.deltaY) || 0;
    return scrollAt(x, y, deltaX, deltaY);
  }
  return false;
};
